import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';

import { fetchImportHistory } from '@src/api/actions/dashboard/dataIntegrations';
import paths from '@src/paths';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TYPE_LABELS = {
  email: 'Email',
  sftp: 'SFTP',
  manual: 'Manual',
  api: 'API',
  google_sheet: 'Google Sheet',
};

const TYPE_CHIP_COLORS = {
  email: 'primary',
  sftp: 'info',
  manual: 'default',
  api: 'secondary',
  google_sheet: 'success',
};

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatAbsolute(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelative(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function DataIntegrationHistoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlClientId = searchParams.get('client_id');

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, has_more: false, limit: 100 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [integrationType, setIntegrationType] = useState('all');
  const [query, setQuery] = useState('');

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (urlClientId) params.filter_client = urlClientId;
      if (integrationType !== 'all') params.integration_type = integrationType;
      if (query.trim()) params.q = query.trim();
      const res = await fetchImportHistory(params);
      const data = res.data;
      // Backend now returns {results, count, has_more, limit}. Tolerate
      // the legacy plain-array shape in case any older caller is cached.
      if (Array.isArray(data)) {
        setRows(data);
        setMeta({ count: data.length, has_more: false, limit: 100 });
      } else {
        setRows(data?.results || []);
        setMeta({
          count: data?.count ?? 0,
          has_more: !!data?.has_more,
          limit: data?.limit ?? 100,
        });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load import history.');
    } finally {
      setLoading(false);
    }
  }, [urlClientId, integrationType, query]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Count per integration type in the currently loaded rows — useful as a
  // quick sanity check ("did I actually get any emails this week?").
  const typeCounts = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      const t = r.integration_type || 'unknown';
      out[t] = (out[t] || 0) + 1;
    });
    return out;
  }, [rows]);

  const typeCountChips = ['email', 'manual', 'sftp'].filter((t) => typeCounts[t]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(paths.dataIntegrations.health)}
            sx={{ mb: 1 }}
          >
            Back to Integrations
          </Button>
          <Typography variant="h4" fontWeight={700}>
            Import History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recent data files processed by your integrations (most recent {meta.limit}).
          </Typography>
        </Box>
      </Stack>

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by filename…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Integration type</InputLabel>
            <Select
              value={integrationType}
              label="Integration type"
              onChange={(e) => setIntegrationType(e.target.value)}
            >
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="email">Automated Email</MenuItem>
              <MenuItem value="manual">Manual Upload</MenuItem>
              <MenuItem value="sftp">SFTP</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {loading ? '…' : `${meta.count} result${meta.count === 1 ? '' : 's'}`}
            {meta.has_more ? ' (capped — refine filters to see older files)' : ''}
          </Typography>
        </Stack>
        {!loading && typeCountChips.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {typeCountChips.map((t) => (
              <Chip
                key={t}
                size="small"
                variant="outlined"
                color={TYPE_CHIP_COLORS[t] || 'default'}
                label={`${TYPE_LABELS[t] || t}: ${typeCounts[t]}`}
              />
            ))}
          </Stack>
        )}
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
              {query || integrationType !== 'all'
                ? 'No imports match your filters.'
                : 'No import history found yet.'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {query || integrationType !== 'all'
                ? 'Try clearing filters or adjusting your search.'
                : 'Once an integration runs, files will show up here automatically.'}
            </Typography>
            {(query || integrationType !== 'all') && (
              <Button
                size="small"
                sx={{ mt: 2 }}
                onClick={() => { setQuery(''); setIntegrationType('all'); }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Card>
      ) : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Filename</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">File Size</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const ts = row.processed_at || row.created;
                  const type = row.integration_type || 'unknown';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2">{formatAbsolute(ts)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelative(ts)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={TYPE_LABELS[type] || type}
                          color={TYPE_CHIP_COLORS[type] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.client_name || '—'}</TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 13,
                          maxWidth: 260,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.filename || ''}
                      >
                        {row.filename || '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 240,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'text.secondary',
                          fontSize: 13,
                        }}
                        title={row.source || ''}
                      >
                        {row.source || '—'}
                      </TableCell>
                      <TableCell align="right">{formatBytes(row.file_size)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
